<template>
  <div v-if="!isHidden">
    <label v-if="showLabel" :for="propDef?.name" class="block text-sm font-medium leading-6 text-gray-900">{{ propDef?.title }}</label>
    <div v-if="propDef?.description" class="text-sm">{{ propDef?.description }}</div>
    <div class="mt-2">
      <div v-if="propDef?.type === 'separator'" class="w-full border-b text-xs">{{ propDef?.hint || '' }}</div>
      <textarea
        v-else-if="propDef?.type === 'text'"
        :id="propDef?.name"
        v-model="value"
        :rows="propDef?.rows || 2"
        :placeholder="propDef?.placeholder || ''"
        class="block w-full rounded-md border-0 p-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:py-1.5 sm:text-sm sm:leading-6"
      />
      <v-ace-editor v-else-if="propDef?.type === 'script'" v-model:value="value" lang="javascript" theme="monokai" :options="{ fontSize: 16, tabSize: 2, useSoftTabs: true, wrap: false }" style="height: 300px" />
      <div v-else-if="propDef?.type === 'number' || propDef?.type === 'integer'" class="relative rounded-md shadow-sm">
        <div v-if="propDef?.prefix" class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <span class="text-gray-500 sm:text-sm">$</span>
        </div>
        <input
          :id="propDef?.name"
          v-model="value"
          type="number"
          :placeholder="propDef?.placeholder || ''"
          class="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          :class="{ 'pl-7': propDef?.prefix, 'pr-18': propDef?.suffix }"
        />
        <div v-if="propDef?.suffix" class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <span id="price-currency" class="text-gray-500 sm:text-sm">{{ propDef?.suffix }}</span>
        </div>
      </div>
      <div v-else-if="propDef?.type === 'string' || propDef?.type === 'stringLong'" class="relative rounded-md shadow-sm">
        <input
          :id="propDef?.name"
          ref="strElm"
          v-model="value"
          :type="propDef?.extraType || 'text'"
          :placeholder="propDef?.placeholder || ''"
          @input="
            (e) => {
              Object.assign(propDef, { valid: propDef?.extraType === 'email' && e.target.value === 'default' ? true : e.target.validity.valid, saved: false });
            }
          "
          class="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          :class="{ 'pl-7': propDef?.prefix, 'pr-18': propDef?.suffix, 'ring-red-500 focus:ring-red-500': !propDef.valid && propDef.saved }"
        />
      </div>
      <div v-else-if="propDef?.type === 'csvAlphaNumeric'" class="relative rounded-md shadow-sm">
        <input
          :id="propDef?.name"
          v-model="value"
          type="text"
          :placeholder="propDef?.placeholder || ''"
          class="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          :class="{ 'pl-7': propDef?.prefix, 'pr-18': propDef?.suffix, 'ring-red-500 focus:ring-red-500': errorMessage }"
        />
        <div v-if="errorMessage" class="mt-2 flex items-center rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-500 dark:border-red-500 dark:bg-gray-800 dark:text-red-400" role="alert">
          <div>{{ errorMessage }}</div>
        </div>
      </div>
      <div v-else-if="propDef?.type === 'select' || propDef?.type === 'selectLong'" class="relative rounded-md shadow-sm">
        <select
          :id="propDef?.name"
          v-model="value"
          class="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
        >
          <option v-for="(opt, idx) in propDef?.options || []" :key="idx" :value="'values' in propDef ? propDef.values[idx] : opt">{{ opt }}</option>
        </select>
      </div>
      <div v-else-if="propDef?.type === 'boolean'" class="relative flex items-center rounded-md">
        <input :id="propDef?.name" v-model="value" type="checkbox" class="size-4 rounded text-indigo-600 focus:ring-indigo-600" />
        <label :for="propDef?.name" class="block pl-2 text-sm font-medium leading-6 text-gray-900">{{ propDef?.title }}</label>
      </div>
      <div v-else-if="propDef?.type === 'audioFile'" class="relative rounded-md shadow-sm">
        <AudioSelector :id="propDef?.name" v-model="value"></AudioSelector>
      </div>
      <div v-else-if="propDef?.type === 'transcriptsCollection'" class="relative rounded-md shadow-sm">
        <CollectionSelector :id="propDef?.name" v-model="value" type="transcripts"></CollectionSelector>
      </div>
      <div v-else-if="propDef?.type === 'recordingsCollection'" class="relative rounded-md shadow-sm">
        <CollectionSelector :id="propDef?.name" v-model="value" type="recordings"></CollectionSelector>
      </div>
      <div v-else-if="propDef?.type === 'kpiCollection'" class="relative rounded-md shadow-sm">
        <CollectionSelector :id="propDef?.name" v-model="value" type="kpi"></CollectionSelector>
      </div>
      <div v-else-if="propDef?.type === 'categoriesCollection'" class="relative rounded-md shadow-sm">
        <CollectionSelector :id="propDef?.name" v-model="value" type="categories"></CollectionSelector>
      </div>
      <div v-else-if="propDef?.type === 'logo'" class="relative rounded-md shadow-sm">
        <LogoSelector :id="propDef?.name" v-model="value"></LogoSelector>
      </div>
      <div v-else-if="propDef?.type === 'temperature'" class="relative rounded-md px-1 shadow-sm">
        <div class="flex items-center justify-between">
          <div class="block text-sm leading-6 text-gray-900">More Strict</div>
          <div class="text-md block shrink font-medium leading-6 text-gray-900">{{ propDef?.title }}: {{ value.toFixed(2) }}</div>
          <div class="block text-sm leading-6 text-gray-900">More Creative</div>
        </div>
        <Slider :id="propDef?.name" v-model="value" :min="0" :max="1" :step="0.05" class="w-14rem mt-2" />
      </div>
      <div v-else-if="propDef?.type === 'numberSlider'" class="relative rounded-md px-1 shadow-sm">
        <div class="flex items-center justify-between">
          <div class="block text-sm leading-6 text-gray-900">Less</div>
          <div class="text-md block shrink font-medium leading-6 text-gray-900">{{ propDef?.title }}: {{ value.toFixed(0) }}</div>
          <div class="block text-sm leading-6 text-gray-900">More</div>
        </div>
        <Slider :id="propDef?.name" v-model="value" :min="propDef?.min || 0" :max="propDef?.max || 100" :step="propDef?.step || 1" class="w-14rem mt-2" />
      </div>
      <div v-else-if="propDef?.type === 'color'" class="relative rounded-md shadow-sm">
        <div class="flex items-center">
          <color-picker-selector v-if="propDef.type === 'color'" v-model="value" :propDef="propDef" :showResult="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, watch, onMounted } from 'vue';
  import AudioSelector from '@/components/Audio/AudioSelector.vue';
  import CollectionSelector from '@/components/Collections/CollectionSelector.vue';
  import LogoSelector from '@/components/Selectors/LogoSelector.vue';
  import ColorPickerSelector from '@/components/Selectors/ColorPickerSelector.vue';
  import Slider from 'primevue/slider';
  import { VAceEditor } from 'vue3-ace-editor';
  import 'ace-builds/src-noconflict/mode-javascript';
  import 'ace-builds/src-noconflict/theme-crimson_editor';
  import 'ace-builds/src-noconflict/theme-monokai';
  import 'ace-builds/src-noconflict/ext-searchbox';

  const props = defineProps(['modelValue', 'propDef']);
  const emit = defineEmits(['update:modelValue', 'update:valid']);

  const isHidden = computed(() => 'hidden' in props.propDef && props.propDef.hidden);
  const showLabel = computed(() => {
    const t = props.propDef?.title || '';
    return t !== '' && !['boolean', 'temperature'].includes(props.propDef?.type);
  });

  const value = computed({
    get() {
      return props.modelValue;
    },
    set(value) {
      emit('update:modelValue', value);
    },
  });

  const errorMessage = ref('');
  const strElm = ref();

  const fieldId = Symbol(props.propDef?.name);

  function validateCommaList(val) {
    errorMessage.value = '';
    if (!val) return emit('update:valid', { id: fieldId, valid: true });

    const allowedPattern = /^[\w\s-]+$/;
    const hasInvalidItem = val.split(',').some((item) => {
      const trimmed = item.trim();
      return trimmed !== '' && !allowedPattern.test(trimmed);
    });

    errorMessage.value = hasInvalidItem ? 'Each item must contain only letters, numbers, underscores, and spaces.' : '';
    emit('update:valid', { id: fieldId, valid: !hasInvalidItem });
  }

  // Validate on page render (and whenever `value` changes)
  watch(
    value,
    (newVal) => {
      if (props.propDef?.type === 'csvAlphaNumeric') {
        validateCommaList(newVal);
      }
    },
    { immediate: true },
  );
  onMounted(() => {
    if (props.propDef?.type === 'csvAlphaNumeric') {
      validateCommaList(value.value);
    }
  });
</script>
